import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/apiService";
import { STAGE_CONFIG } from "../services/apiConstant";
import type { PhaseDefinition, StageDefinition, StageStatus } from "../types/stageConfig";

const STALE_TIME = 10 * 60 * 1000; // 10 minutes — config changes rarely

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

export const usePhases = () =>
  useQuery<PhaseDefinition[]>({
    queryKey: ["stage-config", "phases"],
    queryFn: () => apiService.get<PhaseDefinition[]>(STAGE_CONFIG.phases),
    staleTime: STALE_TIME,
  });

export const useStages = (phaseId?: number) =>
  useQuery<StageDefinition[]>({
    queryKey: ["stage-config", "stages", phaseId ?? null],
    queryFn: () => {
      const url = phaseId != null
        ? `${STAGE_CONFIG.stages}?phase_id=${phaseId}`
        : STAGE_CONFIG.stages;
      return apiService.get<StageDefinition[]>(url);
    },
    staleTime: STALE_TIME,
  });

export const useStageStatuses = (stageId: number | null) =>
  useQuery<StageStatus[]>({
    queryKey: ["stage-config", "statuses", stageId],
    queryFn: () => apiService.get<StageStatus[]>(STAGE_CONFIG.stageStatuses(stageId!)),
    enabled: stageId != null,
    staleTime: STALE_TIME,
  });

export const useAllStatuses = () =>
  useQuery<StageStatus[]>({
    queryKey: ["stage-config", "all-statuses"],
    queryFn: async () => {
      const stages = await apiService.get<StageDefinition[]>(STAGE_CONFIG.stages);
      const allStatuses: StageStatus[] = [];
      const seen = new Set<string>();
      await Promise.all(
        stages.map(async (stage) => {
          const statuses = await apiService.get<StageStatus[]>(
            STAGE_CONFIG.stageStatuses(stage.id)
          );
          for (const s of statuses) {
            if (!seen.has(s.name)) {
              seen.add(s.name);
              allStatuses.push(s);
            }
          }
        })
      );
      return allStatuses;
    },
    staleTime: STALE_TIME,
  });

export const useStatusesForStageIds = (stageIds: number[]) =>
  useQuery<StageStatus[]>({
    queryKey: ["stage-config", "statuses-for-stages", [...stageIds].sort()],
    queryFn: async () => {
      const allStatuses: StageStatus[] = [];
      const seen = new Set<string>();
      await Promise.all(
        stageIds.map(async (stageId) => {
          const statuses = await apiService.get<StageStatus[]>(
            STAGE_CONFIG.stageStatuses(stageId)
          );
          for (const s of statuses) {
            if (!seen.has(s.name)) {
              seen.add(s.name);
              allStatuses.push(s);
            }
          }
        })
      );
      return allStatuses;
    },
    enabled: stageIds.length > 0,
    staleTime: STALE_TIME,
  });

// ---------------------------------------------------------------------------
// Phase mutations
// ---------------------------------------------------------------------------

export const useCreatePhase = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; sort_order: number }) =>
      apiService.post<PhaseDefinition>(STAGE_CONFIG.phases, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "phases"] });
    },
  });
};

export const useUpdatePhase = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name?: string; sort_order?: number }) =>
      apiService.put<PhaseDefinition>(STAGE_CONFIG.phase(id), data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "phases"] });
    },
  });
};

export const useDeletePhase = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiService.delete(STAGE_CONFIG.phase(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "phases"] });
      qc.invalidateQueries({ queryKey: ["stage-config", "stages"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Stage mutations
// ---------------------------------------------------------------------------

export const useCreateStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      phase_id: number;
      name: string;
      sort_order: number;
      expected_days?: number | null;
      requires_description?: boolean;
    }) => apiService.post<StageDefinition>(STAGE_CONFIG.stages, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "stages"] });
    },
  });
};

export const useUpdateStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      name?: string;
      sort_order?: number;
      expected_days?: number | null;
      requires_description?: boolean;
    }) => apiService.put<StageDefinition>(STAGE_CONFIG.stage(id), data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "stages"] });
    },
  });
};

export const useDeleteStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiService.delete(STAGE_CONFIG.stage(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stage-config", "stages"] });
    },
  });
};

// ---------------------------------------------------------------------------
// Status mutations
// ---------------------------------------------------------------------------

export const useCreateStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stageId,
      ...data
    }: {
      stageId: number;
      name: string;
      sort_order: number;
    }) => apiService.post<StageStatus>(STAGE_CONFIG.stageStatuses(stageId), data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["stage-config", "statuses", variables.stageId] });
      qc.invalidateQueries({ queryKey: ["stage-config", "all-statuses"] });
    },
  });
};

export const useUpdateStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, sort_order }: { id: number; stageId: number; name?: string; sort_order?: number }) =>
      apiService.put<StageStatus>(STAGE_CONFIG.status(id), { name, sort_order }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["stage-config", "statuses", variables.stageId] });
      qc.invalidateQueries({ queryKey: ["stage-config", "all-statuses"] });
    },
  });
};

export const useDeleteStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; stageId: number }) =>
      apiService.delete(STAGE_CONFIG.status(id)),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["stage-config", "statuses", variables.stageId] });
      qc.invalidateQueries({ queryKey: ["stage-config", "all-statuses"] });
    },
  });
};
